import { useEffect, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  Modal,
  Link,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';

interface DatePickerConfig {
  id: string;
  is_active: boolean;
  title: string;
  description: string;
}

interface ThankYouConfig {
  id: string;
  type: 'confirmation_page' | 'email';
  is_active: boolean;
  title: string;
  description: string;
}

const PICKER_CONFIGS: DatePickerConfig[] = [
  {
    id: 'general',
    is_active: true,
    title: 'General style',
    description: 'Customize colors, fonts, and overall appearance.',
  },
  {
    id: 'cart_page',
    is_active: false,
    title: 'Cart page date picker',
    description: 'Customize text, placeholders and cart behaviors.',
  },
  {
    id: 'cart_drawer',
    is_active: false,
    title: 'Cart drawer date picker',
    description: 'Customize text, placeholders and cart behaviors.',
  },
  {
    id: 'product_page',
    is_active: false,
    title: 'Product page date picker',
    description: 'Customize text and placeholders.',
  },
  {
    id: 'checkout',
    is_active: true,
    title: 'Checkout date picker',
    description: 'Customize text and checkout behaviors.',
  },
];

const THANK_YOU_CONFIGS: ThankYouConfig[] = [
  {
    id: 'confirmation_page',
    type: 'confirmation_page',
    is_active: false,
    title: 'Delivery Date Confirmation',
    description: 'Show delivery date on order confirmation page.',
  },
  {
    id: 'email',
    type: 'email',
    is_active: false,
    title: 'Email Confirmation',
    description: 'Add delivery date to order confirmation emails.',
  },
];

export default function CustomiseStorefront() {
  const { fetchApi, error } = useApi();
  const { currentShop } = useAuth();
  const [setup, setSetup] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(true);
  const domain = currentShop?.domain || '';
  const shopAdmin = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(domain) ? `https://${domain}/admin` : null;
  const editorUrl = shopAdmin && (setup === 'checkout' || setup === 'confirmation_page' ? `${shopAdmin}/settings/checkout` : setup === 'email' ? `${shopAdmin}/settings/notifications/customer/order_confirmation` : `${shopAdmin}/themes/current/editor${setup === 'product_page' ? '?template=product' : setup === 'cart_page' || setup === 'cart_drawer' ? '?template=cart' : ''}`);
  const [pickerConfigs, setPickerConfigs] = useState<DatePickerConfig[]>(PICKER_CONFIGS);
  const [thankYouConfigs, setThankYouConfigs] = useState<ThankYouConfig[]>(THANK_YOU_CONFIGS);

  useEffect(() => {
    loadConfigs();
  }, []);

  async function loadConfigs() {
    const data = await fetchApi<{ pickers: DatePickerConfig[]; thankYou: ThankYouConfig[] }>('/storefront/config');
    if (data) {
      if (data.pickers?.length) setPickerConfigs(data.pickers);
      if (data.thankYou?.length) setThankYouConfigs(data.thankYou);
    }
  }

  const renderPickerCard = (config: DatePickerConfig) => {
    const isGeneral = config.id === 'general';

    return (
      <Card key={config.id}>
        <BlockStack gap="300">
          {/* Preview Box */}
          <div
            style={{
              background: '#f6f6f7',
              borderRadius: '8px',
              padding: '20px',
              minHeight: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isGeneral ? (
              <BlockStack gap="200">
                <div style={{ background: '#e1e3e5', height: '12px', width: '150px', borderRadius: '4px' }} />
                <div style={{ background: '#e1e3e5', height: '12px', width: '200px', borderRadius: '4px' }} />
                <div style={{ background: '#e1e3e5', height: '12px', width: '180px', borderRadius: '4px' }} />
              </BlockStack>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    background: '#1a1a1a',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    fontSize: '12px',
                  }}
                >
                  Select a delivery date
                  <br />
                  <span style={{ fontSize: '10px', opacity: 0.7 }}>12 May</span>
                </div>
              </div>
            )}
          </div>

          {/* Title and Status */}
          <InlineStack align="space-between">
            <Text as="h3" variant="headingMd">{config.title}</Text>
            {!isGeneral && (
              <Badge>Check in Shopify</Badge>
            )}
          </InlineStack>

          {/* Description */}
          <Text as="p" tone="subdued">{config.description}</Text>

          {/* Action */}
          <Button onClick={() => setSetup(config.id)}>Customize</Button>
        </BlockStack>
      </Card>
    );
  };

  const renderThankYouCard = (config: ThankYouConfig) => {
    const isConfirmationPage = config.type === 'confirmation_page';

    return (
      <Card key={config.id}>
        <BlockStack gap="300">
          {/* Preview Box */}
          <div
            style={{
              background: isConfirmationPage ? '#f0fdf4' : '#faf5ff',
              borderRadius: '8px',
              padding: '16px',
              minHeight: '100px',
            }}
          >
            {isConfirmationPage ? (
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center">
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#22c55e',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                    }}
                  >
                    ✓
                  </div>
                  <Text as="span" variant="bodySm">Confirmation #ABC123EXAMPLE</Text>
                </InlineStack>
                <Text as="p" variant="headingMd">Thank you, Porter!</Text>
                <div
                  style={{
                    border: '2px dashed #22c55e',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '8px',
                  }}
                >
                  <span>🚚</span>
                  <Text as="span" variant="bodyMd">Estimated Delivery: Thu, Apr 3</Text>
                </div>
              </BlockStack>
            ) : (
              <BlockStack gap="200">
                <Text as="p" variant="headingMd">Thank you for your purchase!</Text>
                <Text as="p" variant="bodySm" tone="subdued">We're getting your order ready to be shipped.</Text>
                <div
                  style={{
                    border: '2px dashed #a855f7',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    display: 'inline-block',
                  }}
                >
                  <Text as="span" variant="bodySm">Estimated delivery: Friday, April 18, 2025</Text>
                </div>
                <Text as="p" variant="bodySm" tone="subdued">Example confirmation preview</Text>
              </BlockStack>
            )}
          </div>

          {/* Title and Status */}
          <InlineStack align="space-between">
            <Text as="h3" variant="headingMd">{config.title}</Text>
            <Badge>Check in Shopify</Badge>
          </InlineStack>

          {/* Description */}
          <Text as="p" tone="subdued">{config.description}</Text>
          <Button onClick={() => setSetup(config.id)}>Customize</Button>
        </BlockStack>
      </Card>
    );
  };

  return (
    <Page title="Customise storefront">
      <Layout>
        {/* Help Banner */}
        {showHelp && <Layout.Section>
          <Banner tone="info" onDismiss={() => setShowHelp(false)}>
            <Text as="p" variant="bodyMd">
              <strong>Need help customizing your storefront?</strong>
            </Text>
            <Text as="p" variant="bodySm">
              We're happy to help you activate the app on the storefront and make any styling changes. Free of charge.
            </Text>
            <div style={{ marginTop: 12 }}><InlineStack gap="300" blockAlign="center">
              <Button onClick={() => setSetup('help')}>Request setup help</Button>
              <Badge>By request</Badge>
            </InlineStack></div>
          </Banner>
        </Layout.Section>}

        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Date Picker Customization */}
        <Layout.Section>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">Customise your datepicker style, text and position</Text>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '16px',
              }}
            >
              {pickerConfigs.map((config) => renderPickerCard(config))}
            </div>
          </BlockStack>
        </Layout.Section>

        {/* Thank You Page */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Thank You Page</Text>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '16px',
                }}
              >
                {thankYouConfigs.map(config => renderThankYouCard(config))}
              </div>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
      <Modal open={setup !== null} onClose={() => setSetup(null)} title={setup === 'help' ? 'Request storefront setup help' : 'Customize in Shopify'} secondaryActions={[{ content: 'Close', onAction: () => setSetup(null) }]}><Modal.Section><BlockStack gap="400">
        {setup === 'help' ? <><Text as="p">Email support with your store name and the page you want to set up. We will arrange a suitable time with you.</Text><Button url="mailto:support@whenlyapp.com?subject=Whenly%20storefront%20setup%20help">Email setup support</Button><Button url="/help">Open Whenly help</Button></> : !shopAdmin ? <><Banner>This company does not have a Shopify store selected. Connect a store before customizing its storefront.</Banner><Button url="/settings">Open company settings</Button></> : <>
          <Text as="p">Store: {domain}. Changes are previewed and saved in Shopify. The cards here are examples; they do not confirm whether a block is active.</Text>
          {setup === 'checkout' || setup === 'confirmation_page' ? <><Text as="p">Under Configurations, choose Customize or Edit. Select the page, open Apps, and add or select the available Whenly block. Set its options and Save.</Text><Banner>Apps inside the information, shipping and payment steps require Shopify Plus. Thank you and order status availability depends on the installed extension and Shopify plan.</Banner><Link external url="https://help.shopify.com/en/manual/checkout-settings/customize-checkout-configurations/checkout-editor">Shopify checkout editor guide</Link></> : setup === 'email' ? <><Text as="p">Edit the order confirmation email in Shopify Notifications. Back up the template, then add the selected delivery date from the order attributes and preview a test email before saving.</Text><Text as="p">Date selection uses the Delivery-Date order attribute. Adding it to an email does not add a customer rescheduling link.</Text></> : <><Text as="p">In the theme editor, open the cart or product template and select the Whenly Delivery Date Picker block. If it is missing, choose Add block, Apps, then Whenly. Set the heading, subheading, colour, display style and date requirement, then Save.</Text>{setup === 'cart_drawer' && <Banner>Cart drawers support app blocks only in compatible themes. If your drawer has no Add block option, use the cart page picker or ask setup support to check the theme.</Banner>}</>}
          <Button url={editorUrl || undefined} external>Open Shopify {setup === 'checkout' || setup === 'confirmation_page' ? 'checkout settings' : setup === 'email' ? 'email template' : 'theme editor'}</Button>
        </>}
      </BlockStack></Modal.Section></Modal>
    </Page>
  );
}
